import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';
import { DomainResponse } from '../../store/entities/models/domain-response.model';
import { transformCamelToSnake, transformSnakeToCamel } from '../../store/utils/transformers';

export interface DomainsResponse {
  data: DomainResponse[];
}

export interface UserDomainsMeta {
  currentPage: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface UserDomainsResponse {
  data: DomainResponse[];
  meta: UserDomainsMeta;
}

export interface ProfileDomainsListMeta {
  currentPage: number;
  perPage: number;
  total: number;
  totalPages: number;
}

export interface ProfileDomainsListResponse {
  data: DomainResponse[];
  meta: ProfileDomainsListMeta;
}

export interface UpdateUserDomainBody {
  redirectType?: string;
  notifyDaily?: number;
  notifyWeekly?: number;
  defaultQrTemplateId?: string | null;
  defaultGroups?: string[];
}

export interface DomainGroup {
  uuid: string;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class DomainsService {
  private http = inject(HttpService);
  private readonly domainGroupsCache = new Map<string, Observable<{ data: DomainGroup[] }>>();

  getDomains(): Observable<DomainsResponse> {
    return this.http
      .get<{ data: Record<string, unknown>[] }>('/profile/domains')
      .pipe(
        map((response) => ({
          data: (response.data ?? []).map((row) => transformSnakeToCamel(row) as DomainResponse),
        }))
      );
  }

  getProfileDomainsPage(params: {
    page: number;
    perPage: number;
    search?: string;
  }): Observable<ProfileDomainsListResponse> {
    return this.http
      .get<{ data: Record<string, unknown>[]; meta: Record<string, unknown> }>('/profile/domains', {
        page: params.page,
        per_page: params.perPage,
        search: params.search ?? '',
      })
      .pipe(
        map((response) => ({
          data: (response.data ?? []).map((row) => transformSnakeToCamel(row) as DomainResponse),
          meta: transformSnakeToCamel(response.meta ?? {}) as ProfileDomainsListMeta,
        }))
      );
  }

  getUserDomains(page = 1, perPage = 25): Observable<UserDomainsResponse> {
    return this.http
      .get<{ data: Record<string, unknown>[]; meta: Record<string, unknown> }>('/user/domains', {
        page,
        per_page: perPage,
      })
      .pipe(
        map((response) => ({
          data: (response.data ?? []).map((row) => transformSnakeToCamel(row) as DomainResponse),
          meta: transformSnakeToCamel(response.meta ?? {}) as UserDomainsMeta,
        }))
      );
  }

  updateUserDomain(domainUUID: string, body: UpdateUserDomainBody): Observable<unknown> {
    return this.http.put(`/user/domains/${domainUUID}`, transformCamelToSnake(body));
  }

  getDomainGroups(domainId: string): Observable<{ data: DomainGroup[] }> {
    const cached = this.domainGroupsCache.get(domainId);

    if (cached) {
      return cached;
    }

    const request$ = this.http
      .get<{ data: DomainGroup[] }>(`/domains/${domainId}/groups`)
      .pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.domainGroupsCache.set(domainId, request$);

    return request$;
  }

  /** Call this from group create/update/delete flows to make the next read fresh. */
  invalidateDomainGroups(domainId: string): void {
    this.domainGroupsCache.delete(domainId);
  }

  getDefaultGroups(domainId: string): Observable<{ data: DomainGroup[] }> {
    return this.http.get<{ data: DomainGroup[] }>(
      `/auth/profile/domains/${domainId}/default_groups`
    );
  }
}
